'use client';

import { useSyncExternalStore } from 'react';

/**
 * "Hide amounts" — the website's one privacy switch for giving figures.
 *
 * Shared by every widget that shows someone's own giving: the profile widget
 * (its Giving card, Giving sheet, commitment cards, login-drawer summary) and
 * the See Jesus commitment widget (a commitment already made). One choice,
 * honored everywhere — hide it in one, it's hidden in the other.
 *
 * Hidden by default: giving is private, and these pages are often open on a
 * shared or projected screen. The choice is remembered on this device.
 *
 * The widgets are separate bundles, each with its own copy of this module, and
 * two of them can sit on one page. So a change is broadcast twice over: a
 * window event for other bundles on THIS page, and the `storage` event (fired
 * by the browser) for other tabs. localStorage is a convenience — when it is
 * blocked (private window) the switch still works for the page's lifetime.
 *
 * The storage key predates this module (it was the profile widget's own) and
 * must not change, or everyone's saved choice would reset to hidden.
 */

const KEY = 'wbc-profile:hide-giving-amounts';
const EVENT = 'wbc:hide-giving-amounts';

const listeners = new Set<() => void>();
let hidden: boolean | null = null;

function readStored(): boolean {
  try {
    // Nothing stored yet means the default: hidden.
    return window.localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

function read(): boolean {
  if (hidden === null) hidden = readStored();
  return hidden;
}

function notify() {
  for (const l of listeners) l();
}

let wired = false;
/** Follow changes made by other bundles on this page and by other tabs. */
function wire() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener(EVENT, (e) => {
    const next = (e as CustomEvent<boolean>).detail;
    if (typeof next === 'boolean' && next !== hidden) {
      hidden = next;
      notify();
    }
  });
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    const next = e.newValue !== '0';
    if (next !== hidden) {
      hidden = next;
      notify();
    }
  });
}

export function setAmountsHidden(next: boolean) {
  hidden = next;
  try {
    window.localStorage.setItem(KEY, next ? '1' : '0');
  } catch {
    // Not remembered, still applied.
  }
  notify();
  try {
    window.dispatchEvent(new CustomEvent<boolean>(EVENT, { detail: next }));
  } catch {
    // No CustomEvent (very old browser): other bundles catch up on reload.
  }
}

function subscribe(listener: () => void) {
  wire();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether giving amounts are hidden. Hidden during the server render too, so no figure flashes. */
export function useAmountsHidden(): boolean {
  return useSyncExternalStore(subscribe, read, () => true);
}

/** What a hidden amount reads as. */
export const HIDDEN_AMOUNT = '$•••••';
